import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { ApiError } from '../api/client';
import { type FeedbackKind, type FeedbackStatus, useSendFeedback } from '../api/feedback';
import { useMembership } from '../auth/session';
import { APP_VERSION } from '../lib/version';
import ui from '../styles/ui.module.css';
import { Dialog } from './Dialog';
import styles from './FeedbackDialog.module.css';

const MAX = 4000;

/** A speech bubble with an exclamation mark: "tell us". */
export function FeedbackIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M4 5h16v11H9l-5 4z" />
      <path d="M12 8.5v3.2" />
      <path d="M12 14.2v.01" />
    </svg>
  );
}

export function statusChipClass(status: FeedbackStatus): string | undefined {
  switch (status) {
    case 'NEW':
      return ui.chipNeutral;
    case 'PLANNED':
      return ui.chipSoft;
    case 'DONE':
      return ui.chipReady;
    default:
      return ui.chipNone;
  }
}

/**
 * "Yeni bildirim": the member writes a problem report or a suggestion; the app adds the page, its
 * version, the screen and the language (the server adds the browser, role and part). Their reports,
 * with what became of them, are listed on "Bildirimlerim" (/feedback), linked after sending.
 */
export function FeedbackDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const membership = useMembership();
  const send = useSendFeedback();
  const navigate = useNavigate();
  const [kind, setKind] = useState<FeedbackKind>('DEFECT');
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);

  function close() {
    setSent(false);
    send.reset();
    onClose();
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    send.mutate(
      {
        kind,
        body,
        choirId: membership?.choirId ?? null,
        page: `${location.pathname}${location.search}`,
        appVersion: APP_VERSION,
        screen: `${window.screen.width}x${window.screen.height}, ${window.innerWidth}x${window.innerHeight}`,
        locale: i18n.language,
      },
      {
        onSuccess: () => {
          setBody('');
          setSent(true);
        },
      },
    );
  }

  const error = send.error instanceof ApiError ? send.error.code : send.error ? 'unknown' : null;

  return (
    <Dialog open={open} title={t('feedback.title')} onClose={close} data-testid="feedback-dialog">
      <form className={styles.form} onSubmit={submit} noValidate>
        <p className={styles.intro}>{t('feedback.intro')}</p>
        <div className={styles.kinds} role="radiogroup" aria-label={t('feedback.kindLabel')}>
          {(['DEFECT', 'SUGGESTION'] as const).map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={kind === k}
              className={`${styles.kind} ${kind === k ? styles.kindOn : ''}`}
              onClick={() => {
                setKind(k);
                setSent(false);
              }}
            >
              {t(`feedback.kinds.${k}`)}
            </button>
          ))}
        </div>
        <label className={ui.field}>
          <span className={ui.label}>{kind === 'DEFECT' ? t('feedback.bodyDefect') : t('feedback.bodySuggestion')}</span>
          <textarea
            className={styles.textarea}
            rows={6}
            maxLength={MAX}
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              setSent(false);
            }}
          />
        </label>
        <p className={styles.context}>{t('feedback.context')}</p>
        {sent && (
          <p className={`${ui.notice} ${ui.noticeSuccess}`} role="status">
            {t('feedback.sent')}{' '}
            <button
              type="button"
              className={ui.linkButton}
              onClick={() => {
                close();
                navigate('/feedback');
              }}
            >
              {t('feedback.seeMine')}
            </button>
          </p>
        )}
        {error && (
          <p className={`${ui.notice} ${ui.noticeError}`} role="alert">
            {t(`apiErrors.${error}`, { defaultValue: t('apiErrors.unknown') })}
          </p>
        )}
        <div className={styles.buttons}>
          <button type="button" className={ui.button} onClick={close}>
            {t('common.close')}
          </button>
          <button type="submit" className={`${ui.button} ${ui.primary}`} disabled={!body.trim() || send.isPending}>
            {send.isPending ? t('feedback.sending') : t('feedback.send')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
